import React, { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import MobileNavBar from '../common/MobileNavBar';
import MobileFooter from '../MobileFooter';
import MobileBottomNav from '../common/MobileBottomNav';
import { convertToVwMob } from '../../Utils/responsive';
import { useAppSelector } from '../../store/hooks';
import { selectUserProfile } from '../../Module/User/Presentation/userSelectors';
import KakaoChannelModal from '../common/KakaoChannelModal';
import { useLocation } from 'react-router-dom';
import { PATHS } from '../../routes/paths';

const SUPPRESS_POST_SIGNUP_MODALS_KEY = 'missionfund.suppressPostSignupModals';
const KAKAO_MODAL_SHOWN_STORAGE_KEY = 'missionfund.kakaoModalSeenAfterLogin';


type Props = {
  title?: string;
  children: ReactNode;
  footerRef?: React.Ref<HTMLElement>;
  hideHeader?: boolean;
  hideFooter?: boolean;
  showBottomNav?: boolean;
  /** 스크롤 영역(본문+푸터) 하단 패딩 — 페이지 내 `position:fixed` 하단 바가 있을 때 푸터가 가려지지 않게 */
  mainPaddingBottom?: string;
};

const MobilePageLayout: React.FC<Props> = ({
  title,
  children,
  footerRef,
  hideHeader,
  hideFooter,
  showBottomNav = true,
  mainPaddingBottom,
}) => {
  const location = useLocation();
  const userProfile = useAppSelector(selectUserProfile);
  const [isKakaoModalOpen, setIsKakaoModalOpen] = useState(false);

  useEffect(() => {
    // 로그인, 회원가입 페이지에서는 카카오/프로필 모달 띄우지 않음
    if (
      location.pathname === PATHS.LOGIN ||
      location.pathname === PATHS.SIGNUP ||
      location.pathname === PATHS.PASSWORD_FIND ||
      location.pathname === PATHS.DONATION_HISTORY_FIND
    ) {
      setIsKakaoModalOpen(false);
      return;
    }

    // 회원가입 직후 나의 계정으로 이동한 경우: 모달 비표시. 프로젝트 페이지로 이동 시 플래그 해제
    if (typeof window !== 'undefined' && localStorage.getItem(SUPPRESS_POST_SIGNUP_MODALS_KEY) === '1') {
      if (location.pathname !== PATHS.MY_ACCOUNT && !location.pathname.startsWith(`${PATHS.MY_ACCOUNT}/`)) {
        localStorage.removeItem(SUPPRESS_POST_SIGNUP_MODALS_KEY);
      } else {
        setIsKakaoModalOpen(false);
        return;
      }
    }

    // sns_id가 없으면 항상 모달 표시 (모바일)
    if (userProfile) {
      const hasSnsId = String(userProfile.sns_id ?? '').trim().length > 0;
      if (!hasSnsId) {
        let shouldOpen = true;
        if (typeof window !== 'undefined') {
          const hasShown = localStorage.getItem(KAKAO_MODAL_SHOWN_STORAGE_KEY) === '1';
          shouldOpen = !hasShown;
        }
        if (shouldOpen) {
          if (typeof window !== 'undefined') {
            localStorage.setItem(KAKAO_MODAL_SHOWN_STORAGE_KEY, '1');
          }
          setIsKakaoModalOpen(true);
        }
      } else {
        setIsKakaoModalOpen(false);
      }
    } else {
      // 로그아웃 상태 등 userProfile이 없으면 모달 닫기
      setIsKakaoModalOpen(false);
    }
  }, [userProfile, location.pathname]);

  const handleCloseKakaoModal = () => {
    setIsKakaoModalOpen(false);
  };

  return (
    <PageBackground>
      <Shell>
        {!hideHeader && <MobileNavBar title={title} />}
        <Main showBottomNav={showBottomNav} $paddingBottom={mainPaddingBottom} data-mobile-scroll-main>
          <Content>{children}</Content>
          {!hideFooter && <MobileFooter ref={footerRef} showBottomNav={showBottomNav} />}
        </Main>
        {showBottomNav && <MobileBottomNav />}
      </Shell>
      <KakaoChannelModal isOpen={isKakaoModalOpen} onClose={handleCloseKakaoModal} />
    </PageBackground>
  );
};

const PageBackground = styled.div`
  height: 100dvh;
  background-color: #F9FAFB;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Shell = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Main = styled.main<{ showBottomNav: boolean; $paddingBottom?: string }>`
  flex: 1;
  padding: 0;
  padding-bottom: ${({ $paddingBottom }) => $paddingBottom ?? '0'};
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const Content = styled.div`
  width: 100%;
  min-height: 40vh;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(12)};
  color: ${({ theme }) => theme.colors.grayScale[80]};
  padding: ${convertToVwMob(16)} ${convertToVwMob(16)} ${convertToVwMob(300)};
`;

export default MobilePageLayout;


